import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import ReactECharts from "echarts-for-react";
import DiarySurgeryCard from "./DiarySurgeryCard";

const SurgeryChart = forwardRef(function SurgeryChart(props, ref) {
    const [option, setOption] = useState({});
    const chartRef = useRef(null);
    const surgeries = useRef([])
    const initializeHighlight = useRef(true);
    const surgeryIndex = useRef(0)
    const lastForcedSurgeryIndex = useRef(-1)
    const [tooltipData, setTooltipData] = useState(null);
    const cardVisible = useRef(true);
    const [visibleSurgeries, setVisibleSurgeries] = useState([]);
    const legendItems = useRef([]);
    const [sdfsdfs, setLegendItemsRerender] = useState([])
    const originalSeriesData = useRef({});

    const getSurgeryStyle = surgery => {
        const color = encodeURIComponent(surgery.color)
        if (surgery.isUpdate) {
            return {
                symbol: `image://data:image/svg+xml;utf8,
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                  <circle cx="256" cy="256" r="256" fill="white" stroke="${color}" stroke-width="10"/>
                  <g transform="translate(256,256) scale(18) translate(-12,-12)" stroke="${color}" fill="${color}" stroke-width="0">
                    <path fill="none" d="M0 0h24v24H0z"></path>
                    <path d="M23 8c0 1.1-.9 2-2 2a1.7 1.7 0 0 1-.51-.07l-3.56 3.55c.05.16.07.34.07.52
                             0 1.1-.9 2-2 2s-2-.9-2-2c0-.18.02-.36.07-.52l-2.55-2.55c-.16.05-.34.07-.52.07s-.36-.02-.52-.07l-4.55
                             4.56c.05.16.07.33.07.51 0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2c.18 0 .35.02.51.07l4.56-4.55C8.02
                             9.36 8 9.18 8 9c0-1.1.9-2 2-2s2 .9 2 2c0 .18-.02.36-.07.52l2.55 2.55c.16-.05.34-.07.52-.07s.36.02.52.07l3.55-3.56A1.7
                             1.7 0 0 1 19 8c0-1.1.9-2 2-2s2 .9 2 2z"></path>
                  </g>
                </svg>`,
                symbolSize: 20,
            };
        }
        return {
            symbolSize: 20,
            symbol: `image://data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><circle cx="256" cy="256" r="256" fill="white" stroke="${color}" stroke-width="10"/><path fill="${color}" d="M486.594 19.438l-212.53 169.468c11.276 6.004 20.268 16.362 25.436 28.156 1.943 4.434 3.305 9.2 4 14.094 80.446 34.536 191.193-106.27 183.094-211.72zM252.03 202.125c-1.268.034-2.38.253-4.53.75-4.932 1.14-8.54 2.213-12.03 5L18.22 382.063v27.78l233.218-184.937 11.625 14.656L18.22 433.656v32.53l255.81-204.155c13.41-10.704 14.012-24.534 8.345-37.467-5.662-12.923-18.25-22.68-30.313-22.438h-.03z"/></svg>`
        }
    }
    const isSurgeryVisible = (globalIndex) => {
        if (globalIndex < 0 || globalIndex >= surgeries.current.length) {
            return false;
        }
        const surgery = surgeries.current[globalIndex];
        const legendItem = legendItems.current.find(item => item.id === surgery.id)
        if (!legendItem) {
            return true
        }
        return legendItem.selected
    }

    const findNextVisibleSurgery = (startIndex, direction) => {
        let index = startIndex;
        const increment = direction === 'right' ? 1 : -1;

        while (true) {
            index += increment;

            if (index < 0 || index >= surgeries.current.length) {
                return null;
            }

            if (isSurgeryVisible(index)) {
                return index;
            }
        }
    }

    const updateVisibleSurgeries = () => {
        const visible = surgeries.current.filter((_, index) => isSurgeryVisible(index));
        setVisibleSurgeries(visible);
    }

    const getVisibleSurgeryIndex = (globalIndex) => {
        let visibleIndex = 0;
        for (let i = 0; i < globalIndex; i++) {
            if (isSurgeryVisible(i)) {
                visibleIndex++;
            }
        }
        return visibleIndex;
    }

    const handleLegendClick = (legendID) => {

        // Update legend items state
        legendItems.current = legendItems.current.map(item =>
            item.id === legendID
                ? { ...item, selected: !item.selected }
                : item
        )
        setLegendItemsRerender(legendItems.current)
        const echartsInstance = chartRef.current?.getEchartsInstance();
        if (echartsInstance) {
            const currentOption = echartsInstance.getOption();
            const updatedSeries = currentOption.series.map(s => {
                const surgeryID = s?.data[0]?.surgeryInfo.id
                let currentLegendItem = legendItems.current.find(item => item.id === surgeryID)
                let isSelected = true;
                if (currentLegendItem) {
                    isSelected = currentLegendItem.selected
                }
                return {
                    ...s,
                    data: isSelected
                        ? originalSeriesData.current[s.surgeryID] || s.data
                        : []
                }
            });

            echartsInstance.setOption({
                series: updatedSeries
            });
        }

        updateVisibleSurgeries();
        if (!isSurgeryVisible(surgeryIndex.current)) {
            let nextIndex = findNextVisibleSurgery(surgeryIndex.current, 'right');
            if (nextIndex === null) {
                nextIndex = findNextVisibleSurgery(surgeryIndex.current, 'left');
            }

            if (nextIndex !== null) {
                if (lastForcedSurgeryIndex.current != -1) {
                    dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
                }
                surgeryIndex.current = nextIndex;
                lastForcedSurgeryIndex.current = nextIndex;
                dispatchSurgeryAction("highlight", nextIndex);
                setTooltipData({ "1": "2" });
            }
        }
    }

    const buildOption = (minDate, maxDate) => {
        const surgeriesByDepth = {};
        surgeries.current.forEach((surgery, globalIndex) => {
            if (!surgeriesByDepth[surgery.depth]) {
                surgeriesByDepth[surgery.depth] = [];
            }
            surgeriesByDepth[surgery.depth].push({
                value: [surgery.sharedDate.replace("T", " ").replace("Z", ""), surgery.depth],
                ...getSurgeryStyle(surgery),
                surgeryInfo: surgery,
                globalIndex,
            });
        });

        const handleAddSurgery = () => {
            props.clearTimeRangeFilter()
        };

        const graphic = surgeries.current.length ? undefined :
            [
                {
                    type: 'text',
                    left: 'center',
                    top: '45%',
                    style: {
                        text: 'No surgeries.',
                        fontSize: 18,
                        fill: '#999',
                        textAlign: 'center',
                    }
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '55%',
                    style: {
                        text: 'Clear time range filters',
                        fontSize: 16,
                        fill: '#3498db',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        cursor: 'pointer',
                    },
                    onclick: handleAddSurgery,
                    onmouseover: function () {
                        this.style.fill = '#1d6fa5';
                        this.style.textDecoration = 'underline';
                        this?.__zr?.refresh();
                    },
                    onmouseout: function () {
                        this.style.fill = '#3498db';
                        this.style.textDecoration = 'none';
                        this?.__zr?.refresh();
                    }
                }
            ]

        let series = []
        let newLegendItems = []

        Object.keys(surgeriesByDepth)
            .sort((a, b) => parseFloat(a) - parseFloat(b))
            .forEach(depth => {
                const firstSurgery = surgeriesByDepth[depth][0];
                const seriesColor = firstSurgery.surgeryInfo.color;
                const color = encodeURIComponent(seriesColor)
                let name = firstSurgery.surgeryInfo.displayName
                let surgeryID = firstSurgery.surgeryInfo.id
                let data = surgeriesByDepth[depth]
                data.sort((a, b) => b.globalIndex - a.globalIndex);

                series.push({
                    name: name,
                    surgeryID: surgeryID,
                    type: "scatter",
                    z: 10,
                    symbolSize: 20,
                    itemStyle: {
                        color: seriesColor
                    },
                    symbol: `image://data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><circle cx="256" cy="256" r="256" fill="white" stroke="${color}" stroke-width="20"/><path fill="${color}" d="M486.594 19.438l-212.53 169.468c11.276 6.004 20.268 16.362 25.436 28.156 1.943 4.434 3.305 9.2 4 14.094 80.446 34.536 191.193-106.27 183.094-211.72zM252.03 202.125c-1.268.034-2.38.253-4.53.75-4.932 1.14-8.54 2.213-12.03 5L18.22 382.063v27.78l233.218-184.937 11.625 14.656L18.22 433.656v32.53l255.81-204.155c13.41-10.704 14.012-24.534 8.345-37.467-5.662-12.923-18.25-22.68-30.313-22.438h-.03z"/></svg>`,
                    tooltip: { show: false },
                    emphasis: {
                        scale: 2,
                        itemStyle: {}
                    },
                    data: data
                })

                originalSeriesData.current[surgeryID] = data


                newLegendItems.push({
                    name: name,
                    color: seriesColor,
                    id: surgeryID,
                    selected: true
                })
            })


        legendItems.current = newLegendItems
        setLegendItemsRerender(legendItems.current)

        return {
            legend: {
                show: false
            },
            xAxis: {
                type: "time",
                min: minDate.toISOString().replace("T", " ").replace("Z", ""),
                max: maxDate.toISOString().replace("T", " ").replace("Z", "")
            },
            boundaryGap: true,
            yAxis: {
                type: 'value',
                axisLabel: { show: false },
                scale: true,
            },
            grid: { top: "10%", left: "5%", right: "5%", bottom: "3%", containLabel: true },
            series,
            ...(graphic !== undefined ? { graphic } : {}),
        }
    }

    const closeCard = () => {
        cardVisible.current = false
        if (lastForcedSurgeryIndex.current != -1) {
            dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
        }
        setTooltipData({ "1": "2" })
    }

    const surgeryLeftClick = () => {
        const nextIndex = findNextVisibleSurgery(surgeryIndex.current, 'left');
        if (nextIndex === null) {
            return;
        }
        if (lastForcedSurgeryIndex.current != -1) {
            dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
        }
        dispatchSurgeryAction("downplay", surgeryIndex.current)
        surgeryIndex.current = nextIndex;
        lastForcedSurgeryIndex.current = nextIndex;
        dispatchSurgeryAction("highlight", nextIndex)
        setTooltipData({ "1": "2" })
        updateVisibleSurgeries();
    }

    const surgeryRightClick = () => {
        const nextIndex = findNextVisibleSurgery(surgeryIndex.current, 'right');

        if (nextIndex === null) {
            return;
        }

        if (lastForcedSurgeryIndex.current != -1) {
            dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
        }
        dispatchSurgeryAction("downplay", surgeryIndex.current)
        surgeryIndex.current = nextIndex;
        lastForcedSurgeryIndex.current = nextIndex;
        dispatchSurgeryAction("highlight", nextIndex)
        setTooltipData({ "1": "2" })
        updateVisibleSurgeries();
    }

    const findSeriesAndDataIndex = (globalIndex) => {
        const echartsInstance = chartRef.current?.getEchartsInstance();
        if (!echartsInstance) return null;

        const option = echartsInstance.getOption();
        const series = option.series;

        for (let seriesIndex = 0; seriesIndex < series.length; seriesIndex++) {
            const seriesData = series[seriesIndex].data;
            for (let dataIndex = 0; dataIndex < seriesData.length; dataIndex++) {
                if (seriesData[dataIndex].globalIndex === globalIndex) {
                    return { seriesIndex, dataIndex };
                }
            }
        }
        return null;
    }

    const chartIsReady = (echartsInstance) => {
        echartsInstance.on('mouseover', function (params) {
            cardVisible.current = true
            setTooltipData({ "1": "2" })
            if (params?.mstephens) {
                return
            }

            const globalIndex = params.data.globalIndex;
            if (lastForcedSurgeryIndex.current != -1 && lastForcedSurgeryIndex.current != globalIndex) {
                dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
            }
            lastForcedSurgeryIndex.current = globalIndex
            surgeryIndex.current = globalIndex
            dispatchSurgeryAction("highlight", globalIndex)
            setTooltipData({ "1": "2" })
            updateVisibleSurgeries();
        });

        echartsInstance.on('mouseout', function (params) {
            if (params?.mstephens) {
                return
            }
            if (lastForcedSurgeryIndex.current != -1) {
                dispatchSurgeryAction("highlight", lastForcedSurgeryIndex.current)
            }
            setTooltipData({ "1": "2" })
        });

        echartsInstance.on('finished', function () {
            if (!surgeries.current.length) {
                return
            }
            if (initializeHighlight.current) {
                initializeHighlight.current = false
                let lastVisibleIndex = surgeries.current.length - 1;
                while (lastVisibleIndex >= 0 && !isSurgeryVisible(lastVisibleIndex)) {
                    lastVisibleIndex--;
                }

                if (lastVisibleIndex >= 0) {
                    if (lastForcedSurgeryIndex.current != lastVisibleIndex) {
                        dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
                    }
                    lastForcedSurgeryIndex.current = lastVisibleIndex;
                    surgeryIndex.current = lastVisibleIndex;
                    setTooltipData({ "1": "2" })
                    dispatchSurgeryAction("highlight", lastVisibleIndex)
                    updateVisibleSurgeries();
                }
            }
        })
    }

    const dispatchSurgeryAction = (action, globalIndex) => {
        const echartsInstance = chartRef.current?.getEchartsInstance();

        if (!echartsInstance) {
            console.log("EARLY RETURN ON DISPATCH ACTION")
            return;
        }

        const indices = findSeriesAndDataIndex(globalIndex);
        if (!indices) return;

        echartsInstance.dispatchAction({
            type: action,
            seriesIndex: indices.seriesIndex,
            dataIndex: indices.dataIndex,
            mstephens: true
        })
    }

    useImperativeHandle(ref, () => ({
        update: (newSurgeries, minDate, maxDate) => {
            surgeries.current = newSurgeries || []
            initializeHighlight.current = true
            if (lastForcedSurgeryIndex.current != -1) {
                dispatchSurgeryAction("downplay", lastForcedSurgeryIndex.current)
            }
            const newOption = buildOption(minDate, maxDate);
            if (Object.keys(newOption).length) {
                setOption(newOption)
                const echartsInstance = chartRef.current?.getEchartsInstance();
                if (echartsInstance) {
                    echartsInstance.setOption(newOption, { notMerge: true });
                } else {
                    setOption(newOption);
                }
            };
        },
        getEChartsInstance: () => chartRef.current?.getEchartsInstance?.()
    }), [])

    return (
        <div className="review-card" style={{ marginBottom: '1rem', overflow: 'hidden' }}>
            <h3 style={{ textAlign: 'center' }}>Surgeries</h3>
            {legendItems.current.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    padding: '10px 20px',
                    justifyContent: 'center',
                    marginBottom: '10px'
                }}>
                    {legendItems.current.map(item => (
                        <div
                            key={item.id}
                            onClick={() => handleLegendClick(item.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                opacity: item.selected ? 1 : 0.4,
                                transition: 'opacity 0.2s',
                                userSelect: 'none'
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                width="30"
                                height="30"
                            >
                                <circle cx="256" cy="256" r="256" fill="white" stroke={item.color} strokeWidth="10" />
                                <path
                                    fill={item.color}
                                    d="M486.594 19.438l-212.53 169.468c11.276 6.004 20.268 16.362 25.436 28.156 1.943 4.434 3.305 9.2 4 14.094 80.446 34.536 191.193-106.27 183.094-211.72zM252.03 202.125c-1.268.034-2.38.253-4.53.75-4.932 1.14-8.54 2.213-12.03 5L18.22 382.063v27.78l233.218-184.937 11.625 14.656L18.22 433.656v32.53l255.81-204.155c13.41-10.704 14.012-24.534 8.345-37.467-5.662-12.923-18.25-22.68-30.313-22.438h-.03z"
                                />
                            </svg>
                            <span style={{ fontSize: '14px' }}>{item.name}</span>
                        </div>
                    ))}
                </div>
            )}

            <div style={{ width: "100%", position: 'relative' }}>
                <div style={{ width: "100%", height: "250px", overflow: 'hidden' }}>
                    <ReactECharts
                        ref={chartRef}
                        option={option || {}}
                        style={{ height: "100%" }}
                        onChartReady={(instance) => chartIsReady(instance)}
                        opts={{ replaceMerge: ['graphic'] }}
                    />
                </div>
            </div>
            {visibleSurgeries.length > 0 && (
                <DiarySurgeryCard
                    leftClick={surgeryLeftClick}
                    rightClick={surgeryRightClick}
                    currentIndex={getVisibleSurgeryIndex(surgeryIndex.current)}
                    surgeries={visibleSurgeries}
                    closeCard={closeCard}
                    cardVisible={cardVisible.current}
                />
            )}
        </div>
    )
})

export default SurgeryChart;